import { KM_TO_MI } from 'constants/unit-conversion';
import Sample from 'types/models/sample';
import { NO_WIND, WindDirectionCategory } from 'types/wind.enum';

type WindDirectionResult = { normalisedDegrees: number; category: WindDirectionCategory };

export const getWindDirection = (degrees: number): WindDirectionResult => {
  let category: WindDirectionCategory;
  // Make sure all values fall within the range [0, 360)
  const normalisedDegrees = degrees % 360;
  // We use sixteen categories
  if (normalisedDegrees >= 348.75 || normalisedDegrees < 11.25) {
    category = WindDirectionCategory.N;
  } else if (normalisedDegrees < 33.75) {
    category = WindDirectionCategory.NNE;
  } else if (normalisedDegrees < 56.25) {
    category = WindDirectionCategory.NE;
  } else if (normalisedDegrees < 78.75) {
    category = WindDirectionCategory.ENE;
  } else if (normalisedDegrees < 101.25) {
    category = WindDirectionCategory.E;
  } else if (normalisedDegrees < 123.75) {
    category = WindDirectionCategory.ESE;
  } else if (normalisedDegrees < 146.25) {
    category = WindDirectionCategory.SE;
  } else if (normalisedDegrees < 168.75) {
    category = WindDirectionCategory.SSE;
  } else if (normalisedDegrees < 191.25) {
    category = WindDirectionCategory.S;
  } else if (normalisedDegrees < 213.75) {
    category = WindDirectionCategory.SSW;
  } else if (normalisedDegrees < 236.25) {
    category = WindDirectionCategory.SW;
  } else if (normalisedDegrees < 258.75) {
    category = WindDirectionCategory.WSW;
  } else if (normalisedDegrees < 281.25) {
    category = WindDirectionCategory.W;
  } else if (normalisedDegrees < 303.75) {
    category = WindDirectionCategory.WNW;
  } else if (normalisedDegrees < 326.25) {
    category = WindDirectionCategory.NW;
  } else {
    category = WindDirectionCategory.NNW;
  }

  return { normalisedDegrees, category };
};

type MinimumSampleInfo = Pick<Sample, 'multiDimValues'>;

export const getWindMeasurements = (sample: MinimumSampleInfo) => {
  if (
    !sample.multiDimValues?.sampleDim
    || sample.multiDimValues.sampleDim.length !== 2
  ) {
    return undefined;
  }
  const [speedSample, directionSample] = sample.multiDimValues.sampleDim;
  const windSpeedMetric: number = speedSample.rwValue;
  const windDirectionCategory: WindDirectionCategory | typeof NO_WIND = windSpeedMetric === 0
    ? NO_WIND
    : getWindDirection(directionSample.rwValue).category;

  return { windSpeedMetric, windDirectionCategory };
};

/**
 * Wind speeds are rounded to 1 decimal place for display (defined in SPEED_UNIT_SYMBOL_TO_PRECISION),
 * and values below 0.05 are treated as zero to avoid displaying insignificant values.
 * @param metricValue - The wind speed in metric units (km/h).
 * @param isImperial - If true, converts to imperial units before reducing precision.
 * @returns The wind speed in metric with reduced precision
 */
const getWindSpeed = (metricValue: number, isImperial: boolean): number => {
  /**
  Values below this threshold round to 0 when shown with 1 decimal place.
  The 1 decimal place is defined in SPEED_UNIT_SYMBOL_TO_PRECISION.
  */
  const SIGNIFICANT_THRESHOLD = 0.05;
  const imperialValue: number = metricValue * KM_TO_MI;

  if (isImperial && imperialValue < SIGNIFICANT_THRESHOLD) return 0;
  if (!isImperial && metricValue < SIGNIFICANT_THRESHOLD) return 0;

  return metricValue;
};

export const truncateWindSpeedPrecisionInSample = (sample: Sample, country: string): Sample => {
  if (!sample.multiDimValues?.sampleDim || sample.multiDimValues.sampleDim.length !== 2) return sample;

  const [speedSample] = sample.multiDimValues.sampleDim;
  const windSpeedMetric: number = getWindSpeed(speedSample.rwValue, country == 'USA');

  return {
    ...sample,
    multiDimValues: {
      ...sample.multiDimValues,
      sampleDim: [
        {
          ...speedSample,
          rwValue: windSpeedMetric
        },
        ...(sample.multiDimValues.sampleDim.slice(1))
      ]
    }
  };
};

export const getNormalisedDegreesFromWindDirection = (direction = WindDirectionCategory.N): number => {
  const directionMap: Record<WindDirectionCategory, number> = {
    [WindDirectionCategory.N]: 0,
    [WindDirectionCategory.NNE]: 22.5,
    [WindDirectionCategory.NE]: 45,
    [WindDirectionCategory.ENE]: 67.5,
    [WindDirectionCategory.E]: 90,
    [WindDirectionCategory.ESE]: 112.5,
    [WindDirectionCategory.SE]: 135,
    [WindDirectionCategory.SSE]: 157.5,
    [WindDirectionCategory.S]: 180,
    [WindDirectionCategory.SSW]: 202.5,
    [WindDirectionCategory.SW]: 225,
    [WindDirectionCategory.WSW]: 247.5,
    [WindDirectionCategory.W]: 270,
    [WindDirectionCategory.WNW]: 292.5,
    [WindDirectionCategory.NW]: 315,
    [WindDirectionCategory.NNW]: 337.5
  };

  return directionMap[direction];
};